import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

interface TenantAdminGuardProps {
  children: React.ReactNode;
}

export const TenantAdminGuard = ({ children }: TenantAdminGuardProps) => {
  const navigate = useNavigate();
  const { clientSlug } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [userTenantSlug, setUserTenantSlug] = useState<string | null>(null);

  useEffect(() => {
    const checkTenantAdminAccess = async () => {
      try {
        // Check if user is authenticated
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          console.log("[TenantAdminGuard] No session, redirecting to auth");
          navigate("/auth", { replace: true });
          return;
        }

        // Check if user has admin or staff role
        const { data: hasAdminRole, error: roleError } = await supabase
          .rpc("has_any_admin_role", { _user_id: session.user.id });

        if (roleError) {
          console.error("[TenantAdminGuard] Error checking role:", roleError);
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        if (!hasAdminRole) {
          console.log("[TenantAdminGuard] User is not admin");
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        // Check if user is super_admin (they can access all tenants)
        const { data: isSuperAdmin } = await supabase
          .rpc("is_super_admin", { _user_id: session.user.id });

        if (isSuperAdmin) {
          console.log("[TenantAdminGuard] User is super admin, allowing access");
          setIsAuthorized(true);
          setIsLoading(false);
          return;
        }

        // Check if user is a manager (they can access their organization's clients)
        const { data: isManager } = await supabase
          .rpc("has_manager_role", { _user_id: session.user.id });

        if (isManager) {
          // Get user's organization_id
          const { data: profile } = await supabase
            .from("profiles")
            .select("organization_id")
            .eq("id", session.user.id)
            .single();

          if (profile?.organization_id) {
            // Check if the target client belongs to this organization
            const { data: targetClient } = await supabase
              .from("clients")
              .select("id, slug, organization_id, archived_at")
              .eq("slug", clientSlug)
              .single();

            if (targetClient && 
                targetClient.organization_id === profile.organization_id && 
                !targetClient.archived_at) {
              console.log("[TenantAdminGuard] Manager accessing org client, allowing access");
              setIsAuthorized(true);
              setIsLoading(false);
              return;
            }
          }
          
          console.log("[TenantAdminGuard] Manager cannot access this client");
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        // Get user's client_id from profile
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("client_id")
          .eq("id", session.user.id)
          .single();

        if (profileError || !profile) {
          console.error("[TenantAdminGuard] Error fetching profile:", profileError);
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        // Get client slug from client_id
        const { data: client, error: clientError } = await supabase
          .from("clients")
          .select("slug, archived_at, status")
          .eq("id", profile.client_id)
          .single();

        if (clientError || !client) {
          console.error("[TenantAdminGuard] Error fetching client:", clientError);
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        // Check if client is archived
        if (client.archived_at) {
          console.log("[TenantAdminGuard] Client is archived");
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        // Any non-open status routes the merchant to /checkout so they can
        // (re)activate their subscription. Canonical open statuses are
        // 'trialing' and 'active'; everything else needs a payment path.
        if (
          client.status === "trial_expired" ||
          client.status === "pending_payment" ||
          client.status === "suspended" ||
          client.status === "inactive"
        ) {
          console.log(`[TenantAdminGuard] Client status=${client.status}, redirecting to checkout`);
          navigate(`/checkout?clientId=${profile.client_id}&reason=${client.status}`, { replace: true });
          return;
        }


        setUserTenantSlug(client.slug);

        // Check if user's tenant matches the current route tenant
        if (client.slug !== clientSlug) {
          console.log(`[TenantAdminGuard] Tenant mismatch: user=${client.slug}, route=${clientSlug}`);
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        console.log("[TenantAdminGuard] Access authorized");
        setIsAuthorized(true);
      } catch (error) {
        console.error("[TenantAdminGuard] Error:", error);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkTenantAdminAccess();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        navigate("/auth", { replace: true });
      } else if (event === "SIGNED_IN") {
        // Re-check access on sign in
        checkTenantAdminAccess();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, clientSlug]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Alert className="max-w-md">
          <AlertDescription className="space-y-4">
            <p className="font-medium">Accès non autorisé</p>
            <p className="text-sm text-muted-foreground">
              Vous n'avez pas accès à cet espace. 
              {userTenantSlug && (
                <> Vous êtes autorisé uniquement pour le tenant <strong>{userTenantSlug}</strong>.</>
              )}
            </p>
            <div className="flex gap-2">
              {userTenantSlug && (
                <Button 
                  onClick={() => navigate(`/c/${userTenantSlug}/admin`)}
                  variant="default"
                >
                  Accéder à mon espace
                </Button>
              )}
              <Button 
                onClick={() => navigate("/auth")}
                variant="outline"
              >
                Retour à la connexion
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
};
